import db from "@/db";
import REPORT_STATUS from "@/db/enums/reportStatus";
import { i18nGlobal as i18n } from "@/setup/i18n";
import { reportsApi } from "@/api/clients/reportGeneratorSdk";
import { reportExportRestClient } from "@/api";
import { TASK_ERRORS } from "@/api/utils/waitForTask";
import { isRejection } from "@/utils/network";
import { useReportsStore } from "@/store/modules/reports";
import { useNotificationsStore } from "@/store/modules/socio-store/notification";

i18n.mergeLocaleMessage("fr", {
  successReportGeneration: "Le rapport a été généré avec succès.",
  failReportGeneration:
    "Une erreur s'est produite lors de la génération du rapport, veuillez réessayer.",
  generationTimeout:
    "La génération n'a pas abouti dans le délai imparti ({minutes} min) — le serveur est peut-être indisponible, réessayez plus tard.",
  successExcelExport: "L'export Excel « {0} » est prêt.",
  failExcelExport: "L'export Excel a échoué.",
});
i18n.mergeLocaleMessage("en", {
  successReportGeneration: "The report has been successfully generated.",
  failReportGeneration:
    "An error occurred while generating the report, please try again.",
  generationTimeout:
    "Generation did not complete within the allotted time ({minutes} min) — the server may be unavailable, try again later.",
  successExcelExport: "The Excel export “{0}” is ready.",
  failExcelExport: "The Excel export failed.",
});

const POLL_INITIAL_MS = 1000;
// INFO - A.C - 06/10/2026 - ne pas remonter, voir TPF-2189
const POLL_MAX_MS = 3000;
const POLL_GROWTH = 1.5;
// Un palier d'alerte a 3 min sert a mesurer le temps reel de
// generation en prod : un warning au-dela de 3 min, une erreur a l'abandon.
const POLL_SLOW_MS = 3 * 60 * 1000;
const POLL_TIMEOUT_MS = 20 * 60 * 1000;
// Garde-fou : on ne poll pas un rapport non terminal plus vieux que 1h ; on le
// marque ERROR localement (sinon loader infini sur un rapport mort).
const MAX_AGE_MS = 60 * 60 * 1000;

const POLLABLE_STATUSES = new Set([
  REPORT_STATUS.PENDING,
  REPORT_STATUS.GENERATING,
]);

const EXPORT_RECHECK_DELAY_MS = 30 * 1000;
const EXPECTED_WAIT_ENDINGS = new Set([
  TASK_ERRORS.NOT_PICKED_UP,
  TASK_ERRORS.TIMED_OUT,
]);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Dé-dup : un seul poller par rapport, même si syncPolling repasse.
const activePolls = new Set();
// Générations déclenchées dans CETTE session → notification au statut terminal.
const trackedReports = new Set();

export const trackGeneratedReport = (uuid) => {
  trackedReports.add(uuid);
};

const setReportState = async (uuid, patch) => {
  await db.reports.update(uuid, patch);
  const instance = await db.reports.get({ uuid });
  useReportsStore().upsertListedReport(instance);
};

const refreshReport = async (uuid) => {
  try {
    const report = await reportsApi.retrieve({ uuid });
    await db.models.Report.createOrUpdate({
      ...report,
      revision: report.reportGenerationData?.report
        ? report.reportGenerationData.report.revision_reason
        : "",
    });
  } catch (error) {
    console.error(error);
    return;
  }
  const refreshed = await db.reports.get({ uuid });
  useReportsStore().upsertListedReport(refreshed);
};

const handleTerminal = async (uuid, status, jobStatus) => {
  const isTracked = trackedReports.delete(uuid);

  // Quel que soit le statut terminal, on récupère la ligne réelle côté RG
  // (même un rapport en erreur a son chrono, figé au create), puis on
  // ré-applique l'état terminal local : en cas de timeout de polling, RG
  // connaît encore le rapport comme PENDING et le fetch l'aurait ressuscité.
  await refreshReport(uuid);
  await setReportState(uuid, {
    status,
    generationError: jobStatus?.error ?? null,
  });

  if (!isTracked) return;

  const isSuccess =
    status === REPORT_STATUS.READY || status === REPORT_STATUS.TEMPORARY;
  const notifications = useNotificationsStore();
  const message = i18n.t(
    isSuccess ? "successReportGeneration" : "failReportGeneration"
  );
  if (isSuccess) notifications.showSuccessNotification(message);
  else notifications.showErrorNotification(message);
};

export const pollReport = async (uuid) => {
  if (activePolls.has(uuid)) return;
  activePolls.add(uuid);
  let delay = POLL_INITIAL_MS;
  let slowLogged = false;
  const startedAt = Date.now();
  try {
    while (true) {
      let jobStatus;
      try {
        jobStatus = await reportsApi.jobStatus({ uuid });
      } catch (error) {
        // Erreur transitoire (réseau/SDK) ou 404 : on ne marque PAS ERROR — le
        // rapport sera re-pollé au prochain passage de la liste ; un vrai
        // blocage est attrapé par le timeout ou la règle > 1h de syncPolling.
        console.error(error);
        return;
      }

      const { status } = jobStatus;
      if (!POLLABLE_STATUSES.has(status)) {
        await setReportState(uuid, {
          status,
          documentUuid: jobStatus.documentUuid ?? null,
          generationError: jobStatus.error ?? null,
        });
        await handleTerminal(uuid, status, jobStatus);
        return;
      }

      const elapsed = Date.now() - startedAt;
      if (!slowLogged && elapsed > POLL_SLOW_MS) {
        slowLogged = true;
        console.warn(
          `Rapport ${uuid} : generation toujours en cours apres ${Math.round(
            elapsed / 1000
          )}s (statut ${status}).`
        );
      }

      await setReportState(uuid, { status });
      // INFO - A.C - 06/10/2026 - délai testé après une lecture et pas en tête de boucle : au réveil d'une mise en veille, le rapport a pu aboutir.
      if (elapsed >= POLL_TIMEOUT_MS) break;
      await sleep(delay);
      delay = Math.min(delay * POLL_GROWTH, POLL_MAX_MS);
    }
    // Timeout → ERROR (pas de génération à l'infini).
    console.error(
      `Rapport ${uuid} : generation non aboutie apres ${Math.round(
        POLL_TIMEOUT_MS / 1000
      )}s, abandon du polling.`
    );
    await setReportState(uuid, { status: REPORT_STATUS.ERROR });
    await handleTerminal(uuid, REPORT_STATUS.ERROR, {
      error: i18n.t("generationTimeout", { minutes: POLL_TIMEOUT_MS / 60000 }),
    });
  } finally {
    activePolls.delete(uuid);
  }
};

const notifyTrackedExport = (taskId, isSuccess, name) => {
  if (!trackedReports.delete(taskId)) return;
  const notifications = useNotificationsStore();
  if (isSuccess)
    notifications.showSuccessNotification(i18n.t("successExcelExport", [name]));
  else notifications.showErrorNotification(i18n.t("failExcelExport"));
};

const recheckExportLater = (taskId) =>
  setTimeout(() => pollExport(taskId), EXPORT_RECHECK_DELAY_MS);

const settleUnfinishedExport = async (taskId, error) => {
  const isGone =
    error.response?.status === 404 || !(await db.reports.get({ uuid: taskId }));
  if (isGone) {
    trackedReports.delete(taskId);
    await useReportsStore().forgetReport(taskId);
    return;
  }
  if (error.code !== TASK_ERRORS.FAILED && !isRejection(error)) {
    recheckExportLater(taskId);
    return;
  }
  await setReportState(taskId, {
    status: REPORT_STATUS.ERROR,
    generationError:
      error.detail ?? error.response?.data?.detail ?? i18n.t("failExcelExport"),
  });
  notifyTrackedExport(taskId, false);
};

export const pollExport = async (taskId) => {
  if (activePolls.has(taskId)) return;
  activePolls.add(taskId);
  try {
    const result = await reportExportRestClient.waitForQal2AstExport(taskId);
    await useReportsStore().completeExcelExport(taskId, result);
    notifyTrackedExport(taskId, true, result.name);
  } catch (error) {
    if (!EXPECTED_WAIT_ENDINGS.has(error.code)) console.error(error);
    await settleUnfinishedExport(taskId, error).catch(console.error);
  } finally {
    activePolls.delete(taskId);
  }
};

// Relance le polling pour tous les rapports non terminaux de la liste — à
// appeler au montage de la liste et après chaque fetch : le suivi survit ainsi
// aux refresh et aux navigations entre pages.
export const syncPolling = (reports = []) => {
  const now = Date.now();
  for (const report of reports) {
    if (!POLLABLE_STATUSES.has(report.status)) continue;
    if (report.isExcelExport) {
      pollExport(report.uuid);
      continue;
    }
    const createdAt = report.createdAt
      ? new Date(report.createdAt).getTime()
      : null;
    if (createdAt != null && now - createdAt > MAX_AGE_MS) {
      setReportState(report.uuid, { status: REPORT_STATUS.ERROR }).catch(
        console.error
      );
      continue;
    }
    pollReport(report.uuid);
  }
};
